export type GradebookScoreKind = 'homework' | 'preLecture';

export interface GradebookQuestionMeta {
  id: string;
  index: number;
  // Whether this question counts: assigned per the component's question
  // settings (ALL, USER_DEFINED subset, FIXED/RANDOM count) and not removed from
  // the assignment. Questions that do not count are not scored and are hidden
  // from the single-student grade view.
  isAssigned: boolean;
  // Removed from the assignment by an instructor (SetQuestionRemoved). It stays
  // at its position, because student stats are positional, but never counts.
  isRemoved?: boolean;
  kind: GradebookScoreKind;
  // "Q1", "Q2", …: the questions that count, numbered per kind in chapter
  // order, as students see them.
  label: string;
  maxScore: number;
  // The prompt's opening words as plain text; '' when it has none.
  prompt?: string;
  sectionId: string;
  sectionIndex: number;
  // "Multiple choice", "Numerical", …
  typeLabel?: string;
}

export interface GradebookSectionMeta {
  homeworkQuestions: GradebookQuestionMeta[];
  id: string;
  index: number;
  preLectureQuestions: GradebookQuestionMeta[];
}

// One scorable unit of a Kai Coach with credit: the session, or the activity
// when a session is split. Its score is stored under uid in section_stats.
export interface GradebookCoachUnit {
  label: string;
  points: number;
  uid: string;
}

export interface GradebookAssignment {
  componentId: string;
  componentUid: string;
  hasHomework: boolean;
  // True for a Kai Coach module with participation credit enabled and points
  // authored on at least one unit.
  hasParticipation: boolean;
  hasPreLecture: boolean;
  homeworkMaxScore: number;
  isComplete: boolean;
  isLive: boolean;
  isSectionDivider: boolean;
  isVisibleToStudents: boolean;
  // Sum of points over the coach's currently-visible scorable units (0 for a
  // non-coach assignment).
  participationMaxScore: number;
  // Coach assignments only: the visible units with points, each editable in the
  // student detail page.
  coachUnits?: GradebookCoachUnit[];
  preLectureMaxScore: number;
  sections: GradebookSectionMeta[];
  title: string;
}

export interface QuestionScore {
  // An instructor set this score by hand; absent means grading produced it.
  adjusted?: boolean;
  attempts: number;
  id: string;
  index: number;
  kind: GradebookScoreKind;
  label: string;
  maxScore: number;
  needsGrading: boolean;
  score: number | null;
  sectionId: string;
  sectionStatsId: string;
}

export interface SectionAssignmentScore {
  homeworkScore: number | null;
  id: string;
  preLectureQuestionScores: QuestionScore[];
  preLectureScore: number | null;
  questionScores: QuestionScore[];
  sectionStatsId: string;
}

// A student's score on one coach unit. adjusted: an instructor set it by hand,
// so automatic coach scoring leaves it alone.
export interface CoachUnitScore {
  adjusted: boolean;
  score: number | null;
  unitUid: string;
}

export interface AssignmentScore {
  componentId: string;
  componentTitle: string;
  componentUid: string;
  homeworkPossible: number;
  homeworkScore: number | null;
  // Participation (Kai Coach) earned/possible. null score = nothing earned yet.
  participationPossible: number;
  participationScore: number | null;
  // Coach assignments only: the student's score on each of assignment.coachUnits.
  coachUnitScores?: CoachUnitScore[];
  preLecturePossible: number;
  preLectureQuestionScores: QuestionScore[];
  preLectureScore: number | null;
  questionScores: QuestionScore[];
  sectionScores: SectionAssignmentScore[];
}

export interface GradebookStudentProfile {
  firstName: string;
  instituteId: string;
  lastName: string;
  preferredEmail: string;
  preferredUsername: string;
  rawProfile: Record<string, unknown> | null;
}

export interface GradebookStudent {
  assignmentScores: Record<string, AssignmentScore>;
  email: string;
  firstName: string;
  hasLoadedGrades: boolean;
  id: string;
  instituteId: string;
  lastName: string;
  profile: GradebookStudentProfile;
  totalHomeworkPossible: number;
  totalHomeworkScore: number;
  totalParticipationPossible: number;
  totalParticipationScore: number;
  totalPreLecturePossible: number;
  totalPreLectureScore: number;
  uid?: string;
}

export interface GradebookRoster {
  studentCount: number;
  students: GradebookStudent[];
}

export interface StudentGradesState {
  batchSize: number;
  isComplete: boolean;
  loaded: number;
  students: GradebookStudent[];
  total: number;
}

export function buildQuestionScoreKey(question: Pick<QuestionScore, 'id' | 'index' | 'kind' | 'sectionId'>) {
  return `${question.kind}:${question.sectionId}:${question.id}:${question.index}`;
}

// The edit-draft key for a coach unit's score, beside the question keys.
export function buildCoachUnitKey(unitUid: string) {
  return `coach:${unitUid}`;
}

export function buildQuestionMetaKey(
  question: Pick<GradebookQuestionMeta, 'id' | 'index' | 'kind' | 'sectionId'>,
) {
  return `${question.kind}:${question.sectionId}:${question.id}:${question.index}`;
}

export function formatGradeValue(value: number) {
  if (Number.isInteger(value)) {
    return String(value);
  }

  return value.toFixed(2).replace(/\.?0+$/, '');
}

export function sumQuestionScores(questionScores: QuestionScore[]) {
  return questionScores.reduce(
    (total, question) => total + (question.score ?? 0),
    0,
  );
}

export function sumQuestionPossible(questionScores: Array<Pick<QuestionScore, 'maxScore'>>) {
  return questionScores.reduce((total, question) => total + question.maxScore, 0);
}

export function getStudentDisplayName(student: Pick<GradebookStudent, 'email' | 'firstName' | 'id' | 'lastName'>) {
  const fullName = `${student.firstName} ${student.lastName}`.trim();

  return fullName.length > 0 ? fullName : student.email || student.id;
}

export function getSortableStudentLabel(
  student: Pick<GradebookStudent, 'email' | 'firstName' | 'id' | 'instituteId' | 'lastName'>,
  column: string,
) {
  switch (column) {
    case 'firstName':
      return student.firstName || student.email || student.id;
    case 'email':
      return student.email || student.id;
    case 'instituteId':
      return student.instituteId || student.id;
    case 'lastName':
    default:
      return student.lastName || student.firstName || student.email || student.id;
  }
}
